"use client";

/**
 * Browser side of reCAPTCHA v3.
 *
 * v3 has no widget — no checkbox, nothing to click. The script mints a
 * short-lived token, which we send as `x-captcha-response` for Better Auth's
 * captcha plugin to verify server-side.
 *
 * The site key is fetched from `/api/captcha-config` rather than read from a
 * `NEXT_PUBLIC_` variable. Those are inlined at build time, and the production
 * image is built without secrets, so the literal would be empty no matter what
 * Kubernetes provides at runtime — while the server secret *is* read at
 * runtime. That mismatch would have the server demanding a token the browser
 * could not mint, failing every signup.
 *
 * Both the config and the script load at submit, not on page load: the v3
 * script profiles the whole session, so pulling it in earlier would report
 * every visitor rather than only people creating an account.
 */

const SCRIPT_ID = "recaptcha-v3";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export interface CaptchaConfig {
  siteKey: string | null;
  /** True only when the server also holds a secret and will demand a token. */
  enabled: boolean;
}

let configPromise: Promise<CaptchaConfig> | null = null;

/** Fetched once per page and reused; the answer cannot change mid-session. */
export function getCaptchaConfig(): Promise<CaptchaConfig> {
  configPromise ??= fetch("/api/captcha-config", { cache: "no-store" })
    .then((response) => (response.ok ? response.json() : { siteKey: null, enabled: false }))
    .catch(() => ({ siteKey: null, enabled: false }));

  return configPromise;
}

function loadScript(siteKey: string): Promise<void> {
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");

    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load reCAPTCHA"));

    document.head.appendChild(script);
  });
}

/**
 * A token for one action, or null when reCAPTCHA is not configured.
 *
 * Null rather than throwing keeps the form usable on a deployment with no
 * keys: the server only demands a token when it has a secret to check it
 * against, so the two stay consistent.
 */
export async function getCaptchaToken(action: string): Promise<string | null> {
  const config = await getCaptchaConfig();

  if (!config.siteKey) return null;

  await loadScript(config.siteKey);

  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) throw new Error("reCAPTCHA did not initialise");

  await new Promise<void>((resolve) => grecaptcha.ready(resolve));

  return grecaptcha.execute(config.siteKey, { action });
}
